<template>
    <div class="services-per-user-modal modal-card">
        <header class="modal-card-head">
            <p class="title modal-card-title">{{ row.service_title }}</p>
        </header>
        <section class="modal-card-body">
            <div class="columns filters-container">
                <div class="column is-6-tablet">
                    <o-field>
                        <o-input
                            type="search"
                            v-model="filters.projectName"
                            placeholder="Project Name / Number"
                            icon="search"
                        />
                    </o-field>
                </div>
                <div class="column is-6-tablet">
                    <div class="field">
                        <multicomplete v-model="serviceUsers" type="user" placeholder="Project Manager" />
                    </div>
                </div>
                <div class="column is-6-tablet">
                    <o-field>
                        <autocomplete-select
                                v-model="filters.company_id"
                                :custom="{placeholder: 'Company'}"
                                type="company"
                                @input="fetchPaginatedData(true)"
                        />
                    </o-field>
                </div>
                <div class="column is-6-tablet">
                    <o-field>
                        <o-select
                                v-model="filters.borough"
                                :expanded="true"
                                placeholder="Borough"
                                @input="fetchPaginatedData(true)"
                        >
                            <option
                                    v-for="(b, i) in boroughs"
                                    :value="b.id"
                                    :key="i"
                            >
                                {{ b.name }}
                            </option>
                        </o-select>
                    </o-field>
                </div>
                <div class="column is-6-tablet">
                    <div class="field">
                        <datepicker v-model="filters.from"
                                    input-class="input is-fullwidth"
                                    placeholder="Estimated bill date from" :absolute="true"
                        />
                    </div>
                </div>
                <div class="column is-6-tablet">
                    <datepicker v-model="filters.to"
                                input-class="input is-fullwidth"
                                placeholder="Estimated bill date to" :absolute="true"
                    />
                </div>
            </div>
            <div class="columns">
                <div class="column"></div>
                <div class="column is-2 action-box">
                    <div class="field">
                        <p v-if="isResetFiltersButtonVisible" class="control">
                            <a @click.prevent="resetFilters" class="button is-info is-pulled-right"><span>Reset filters</span></a>
                        </p>
                    </div>
                </div>
            </div>
            <o-table
                class="services-per-user-detail is-responsive is-responsive-xs-desktop"
                v-if="isTableVisible"
                :bordered="table.bordered"
                :checkable="table.checkable"
                :data="data"
                :default-sort="table.defaultSort"
                :default-sort-direction="table.defaultSortDirection"
                :hoverable="table.hoverable"
                :loading="loading"
                :mobile-cards="table.mobileCards"
                :narrowed="table.narrowed"
                :paginated="table.paginated"
                :pagination-simple="table.paginationSimple"
                :per-page="table.perPage"
                :striped="table.striped"
            >
                <template slot-scope="props">
                    <o-table-column
                        field="project_title"
                        label="Project"
                        :sortable="true"
                    >
                        <router-link :to="{ name: 'projects.show.services', params: { slug: props.row.project_id } }">
                            {{ props.row['project_title'] }}
                        </router-link>
                    </o-table-column>
                    <o-table-column
                        :custom-sort="customSort"
                        field="amountLabel"
                        label="Amount"
                        :sortable="true"
                    >{{ props.row.amountLabel }}</o-table-column>
                    <o-table-column
                        field="qty"
                        label="Qty"
                        :sortable="true"
                    >{{ props.row.qty }}</o-table-column>
                    <o-table-column
                        :custom-sort="customSortDaysAvg"
                        field="daysAvg"
                        label="Avg Days"
                        :sortable="true"
                    >{{ Number(props.row.daysAvg).toFixed(2) }}</o-table-column>
                    <o-table-column
                        field="estimated_bill_date"
                        label="Estimated bill date"
                        :sortable="true"
                    >{{ props.row.estimated_bill_date }}</o-table-column>
                    <o-table-column
                        field="user_fullname"
                        label="PM"
                        :sortable="true"
                    >{{ showUserColumn ? props.row.user_fullname : '-' }}</o-table-column>
                </template>
                <template slot="footer">
                    <th>Total</th>
                    <th label="Amount">
                        {{ totalsColumns.amountLabel }}
                    </th>
                    <th label="Qty">
                        {{ totalsColumns.qty }}
                    </th>
                    <th label="Avg Days">
                        {{ totalsColumns.daysAvg }}
                    </th>
                    <th label=""></th>
                    <th label=""></th>
                </template>
                <div slot="empty" class="has-text-centered">
                    This table is empty!
                </div>
            </o-table>
        </section>
        <footer class="modal-card-foot">
            <button class="button" type="button" @click="$parent.$parent.$emit('update:active', false)">Close</button>
        </footer>
    </div>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex'
    import Datepicker from './../Datepicker'
    import Table from './../../mixins/Table'

    export default {
        mixins: [Table],

        components: {
            Datepicker,
        },

        computed: {
            ...mapGetters(['boroughNameById', 'boroughs']),
            showUserColumn() {
                return !(this.serviceUsers.length === 1)
            },

            title() {
                let title = this.row.service_title
                if( this.row.user_fullname ) {
                    title += ': ' + this.row.user_fullname
                }
                return title
            },

            totalsColumns() {
                const currency = this.$filters.currency
                const totalsColumns = { amount: 0, daysAvg: 0, qty: 0 }
                this.data.forEach(i => {
                    totalsColumns.amount += isNaN(i.amount) ? 0 : parseFloat(i.amount)
                    totalsColumns.daysAvg += parseFloat(i.daysAvg) || 0
                    totalsColumns.qty += isNaN(i.qty) ? 0 : parseInt(i.qty)
                })
                totalsColumns.amountLabel = currency(totalsColumns.amount)
                totalsColumns.daysAvg = Number(totalsColumns.daysAvg/(this.data.length || 1)).toFixed(2)
                return totalsColumns
            },
        },

        data () {
            return {
                data: [],
                filters: {
                    from: null,
                    projectName: '',
                    serviceUsers: [],
                    to: null,
                },
                groupByFilter: [
                    "estimated_bill_date",
                    "project",
                    "service",
                    "user",
                ],
                isReminderModalActive: false,
                isTableVisible: true,
                loading: false,
                serviceUsers: [],
                table: {
                    bordered: false,
                    checkable: false,
                    defaultSort: ['amountLabel', 'desc'],
                    defaultSortDirection: 'desc',
                    hoverable: true,
                    mobileCards: true,
                    narrowed: false,
                    paginated: true,
                    paginationSimple: false,
                    perPage: 15,
                    striped: true,
                },
                tableName: 'services-per-user-detail',
            }
        },

        mounted () {
            this.serviceUsers = []
            this.filters.from = this.row.filters.from
            this.filters.serviceUsers = []
            this.filters.to = this.row.filters.to
            if(this.row.user_id) {
                this.serviceUsers = [{id: this.row.user_id, full_name: this.row.user_fullname}]
                this.filters.serviceUsers = [this.row.user_id]
            }
            this.fetchPaginatedData()
        },

        methods: {
            customSort(itemA, itemB, isAsc) {
                let a = parseFloat(itemA.amount), b = parseFloat(itemB.amount)
                return isNaN(a) && !isNaN(b) ? 1 : isNaN(b) && !isNaN(a) ? -1 : isAsc ? (a > b ? 1 : a === b ? 0 : -1) : ( b > a ? 1 : a === b ? 0 : -1 )
            },
            customSortDaysAvg(itemA, itemB, isAsc) {
                let a = parseFloat(itemA.daysAvg), b = parseFloat(itemB.daysAvg)
                return isNaN(a) && !isNaN(b) ? 1 : isNaN(b) && !isNaN(a) ? -1 : isAsc ? (a > b ? 1 : a === b ? 0 : -1) : ( b > a ? 1 : a === b ? 0 : -1 )
            },
            fetchData(filters) {
                this.loading = true
                const services = []
                const service_title = this.row.service_title
                const withoutUsers = this.row.filters.withoutUsers && this.serviceUsers.length === 0
                if(this.row.service_id) {
                    services.push(this.row.service_id)
                }
                const params = { ...this.row.filters, ...filters, groupBy: this.groupByFilter, services, service_title, withoutUsers }
                return axios.get(`/api/reports/services-overview-report`, { params })
                    .then(res => {
                        this.data = res.data.data.map(i => ({
                            ...i,
                            amountLabel: this.$filters.currency(i.amount)
                        }))
                        return res
                    }).finally(res => {
                        this.loading = false
                        this.$forceUpdate()
                        return res
                    })
            },
        },

        props: {
            row: {
                type: Object,
            },
        },

        watch: {
            filters: {
                deep: true,
                handler() {
                    this.fetchPaginatedData()
                },
            },
            'filters.serviceUsers'(v) { if (v.length === 0 && this.serviceUsers.length) this.serviceUsers = [] },
            serviceUsers() { this.filters.serviceUsers = _.map(this.serviceUsers, 'id') },
        },
    }
</script>

<style>
.services-per-user-modal {
  width: 100%; }
.services-per-user-modal .filters-container {
  align-items: center;
  flex-wrap: wrap; }
.services-per-user-modal .services-per-user-detail .table-wrapper {
  min-height: auto;
  overflow-x: auto; }

</style>
